import React from "react";
import { Card, Row, Col, Statistic, Table, Spin, Empty, Tag, Progress, Avatar, Tooltip } from "antd";
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FundOutlined,
} from "@ant-design/icons";

const VideoSection = ({ videoStats, topVideos, timeline, loading }) => {
  return (
    <div>
      {/* Video KPI Cards */}
      <VideoKPIs stats={videoStats} loading={loading.videos} />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* Drop-Off Funnel */}
        <Col xs={24} lg={8}>
          <DropOffFunnel stats={videoStats} loading={loading.videos} />
        </Col>
        {/* Timeline */}
        <Col xs={24} lg={16}>
          <TimelineTable timeline={timeline} loading={loading.timeline} />
        </Col>
      </Row>

      {/* Top Videos */}
      <div style={{ marginTop: 16 }}>
        <TopVideosTable videos={topVideos} loading={loading.topVideos} />
      </div>
    </div>
  );
};

const VideoKPIs = ({ stats, loading }) => {
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 20 }}>
        <Spin />
      </div>
    );
  }
  if (!stats) return null;

  const formatTime = (seconds) => {
    if (!seconds) return "0h";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours.toLocaleString()}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <Row gutter={[16, 16]}>
      {[
        { title: "Total Sessions", value: stats.totalSessions, icon: <PlayCircleOutlined />, color: "#1890ff" },
        { title: "Concluidos", value: stats.completedSessions, icon: <CheckCircleOutlined />, color: "#52c41a" },
        { title: "Taxa Conclusao", value: `${stats.completionRate?.toFixed(1)}%`, icon: <FundOutlined />, color: "#13c2c2" },
        { title: "Progresso Medio", value: `${stats.avgProgress?.toFixed(1)}%`, icon: <FundOutlined />, color: "#722ed1" },
        { title: "Tempo Total", value: formatTime(stats.totalWatchTimeSeconds), icon: <ClockCircleOutlined />, color: "#fa8c16" },
        { title: "Usuarios Unicos", value: stats.uniqueUsers, icon: <UserOutlined />, color: "#eb2f96" },
      ].map((item) => (
        <Col xs={12} sm={8} lg={4} key={item.title}>
          <Card size="small" bordered={false}>
            <Statistic
              title={
                <span style={{ fontSize: 12 }}>
                  {React.cloneElement(item.icon, { style: { color: item.color, marginRight: 4 } })}
                  {item.title}
                </span>
              }
              value={item.value}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

const DropOffFunnel = ({ stats, loading }) => {
  if (loading) {
    return (
      <Card title="Drop-Off Funnel" size="small">
        <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
      </Card>
    );
  }
  if (!stats?.dropOff) {
    return (
      <Card title="Drop-Off Funnel" size="small">
        <Empty description="Sem dados" />
      </Card>
    );
  }

  const { dropOff } = stats;
  const total = dropOff.range_0_25 + dropOff.range_25_50 + dropOff.range_50_75 + dropOff.range_75_100;

  const ranges = [
    { label: "0 - 25%", value: dropOff.range_0_25, color: "#ff4d4f" },
    { label: "25 - 50%", value: dropOff.range_25_50, color: "#fa8c16" },
    { label: "50 - 75%", value: dropOff.range_50_75, color: "#fadb14" },
    { label: "75 - 100%", value: dropOff.range_75_100, color: "#52c41a" },
  ];

  return (
    <Card title="Drop-Off Funnel" size="small">
      {ranges.map((range) => {
        const pct = total > 0 ? (range.value / total) * 100 : 0;
        return (
          <div key={range.label} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 13 }}>{range.label}</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                {range.value.toLocaleString()} ({pct.toFixed(1)}%)
              </span>
            </div>
            <Progress
              percent={pct}
              showInfo={false}
              strokeColor={range.color}
              size="small"
            />
          </div>
        );
      })}
    </Card>
  );
};

const TimelineTable = ({ timeline, loading }) => {
  if (loading) {
    return (
      <Card title="Timeline de Views" size="small">
        <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
      </Card>
    );
  }
  if (!timeline?.data || timeline.data.length === 0) {
    return (
      <Card title="Timeline de Views" size="small">
        <Empty description="Sem dados para o periodo" />
      </Card>
    );
  }

  const maxValue = Math.max(...timeline.data.map((d) => Math.max(d.views || 0, d.completed || 0)));

  const renderBar = (value, color) => {
    const width = maxValue > 0 ? (value / maxValue) * 100 : 0;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: `${Math.max(width, 2)}%`,
            minWidth: value > 0 ? "4px" : "0",
            maxWidth: "100%",
            height: "14px",
            backgroundColor: color,
            borderRadius: "2px",
            transition: "width 0.3s ease",
          }}
        />
        <span style={{ fontSize: 12, color: "#666", minWidth: 30 }}>{value}</span>
      </div>
    );
  };

  const columns = [
    { title: "Data", dataIndex: "date", key: "date", width: 100, fixed: "left" },
    {
      title: <Tag color="blue">Views</Tag>,
      dataIndex: "views",
      key: "views",
      render: (val) => renderBar(val || 0, "#1890ff"),
    },
    {
      title: <Tag color="green">Concluidos</Tag>,
      dataIndex: "completed",
      key: "completed",
      render: (val) => renderBar(val || 0, "#52c41a"),
    },
  ];

  return (
    <Card
      title="Timeline de Views"
      size="small"
      extra={
        <span style={{ color: "#888", fontSize: 12 }}>
          {timeline.startDate} a {timeline.endDate} (por {timeline.groupBy})
        </span>
      }
    >
      <Table
        columns={columns}
        dataSource={timeline.data}
        rowKey="date"
        pagination={false}
        size="small"
        scroll={{ x: 500, y: 300 }}
      />
    </Card>
  );
};

const TopVideosTable = ({ videos, loading }) => {
  const columns = [
    {
      title: "Video",
      dataIndex: "videoName",
      key: "videoName",
      ellipsis: true,
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            shape="square"
            size={40}
            src={record.thumbnail || undefined}
            icon={!record.thumbnail && <PlayCircleOutlined />}
          />
          <Tooltip title={name}>
            <span style={{ fontSize: 13 }}>{name}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Views",
      dataIndex: "totalViews",
      key: "totalViews",
      width: 80,
      sorter: (a, b) => a.totalViews - b.totalViews,
    },
    {
      title: "Concluidos",
      dataIndex: "completedViews",
      key: "completedViews",
      width: 100,
      sorter: (a, b) => a.completedViews - b.completedViews,
    },
    {
      title: "Taxa",
      dataIndex: "completionRate",
      key: "completionRate",
      width: 90,
      sorter: (a, b) => a.completionRate - b.completionRate,
      render: (val) => (
        <Tag color={val >= 70 ? "green" : val >= 40 ? "orange" : "red"}>
          {val?.toFixed(1)}%
        </Tag>
      ),
    },
    {
      title: "Progresso",
      dataIndex: "avgProgress",
      key: "avgProgress",
      width: 120,
      render: (val) => (
        <Progress
          percent={val?.toFixed(0)}
          size="small"
          strokeColor={val >= 70 ? "#52c41a" : val >= 40 ? "#fa8c16" : "#ff4d4f"}
        />
      ),
    },
  ];

  return (
    <Card title="Top Videos por Conclusao" size="small">
      <Table
        columns={columns}
        dataSource={videos}
        rowKey="videoId"
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 600 }}
      />
    </Card>
  );
};

export default VideoSection;
